// @spec IAM-STR-029
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OrganizationsFilters,
  OrganizationsTable,
  useAdminOrganizations,
  useExportOrganizations,
} from '@/features/admin';
import type { OrganizationsFiltersType, SortField, SortOrder } from '@/features/admin';

export const Route = createFileRoute('/_authenticated/admin/organizations')({
  component: AdminOrganizationsPage,
});

const DEFAULT_FILTERS: OrganizationsFiltersType = {
  search: '',
  status: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  pageSize: 10,
};

/**
 * Check if user has Super Admin role from Clerk metadata.
 */
function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;

  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

function AdminOrganizationsPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // Check Super Admin access
  const hasAccess = isSuperAdmin(user);

  // Parse initial filters from URL search params
  const [filters, setFilters] = useState<OrganizationsFiltersType>(() => {
    if (typeof window === 'undefined') return DEFAULT_FILTERS;

    const params = new URLSearchParams(window.location.search);
    const search = params.get('search');
    const status = params.get('status') as OrganizationsFiltersType['status'] | null;
    const sortBy = params.get('sortBy') as SortField | null;
    const sortOrder = params.get('sortOrder') as SortOrder | null;
    const page = params.get('page');
    const pageSize = params.get('pageSize');

    return {
      search: search ?? '',
      status: status ?? '',
      sortBy: sortBy ?? 'createdAt',
      sortOrder: sortOrder ?? 'desc',
      page: page ? Number(page) : 1,
      pageSize: pageSize ? Number(pageSize) : 10,
    };
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.sortBy !== 'createdAt') params.set('sortBy', filters.sortBy);
    if (filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);
    if (filters.page !== 1) params.set('page', String(filters.page));
    if (filters.pageSize !== 10) params.set('pageSize', String(filters.pageSize));

    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.replaceState(null, '', newUrl);
  }, [filters]);

  // Fetch organizations
  const { data, isLoading, error } = useAdminOrganizations(filters);

  // Export mutation
  const { mutate: exportCsv, isPending: isExporting } = useExportOrganizations();

  const handleFiltersChange = useCallback((newFilters: OrganizationsFiltersType) => {
    setFilters(newFilters);
  }, []);

  const handleSort = useCallback((sortBy: SortField, sortOrder: SortOrder) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1, // Reset to first page on sort change
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({
      ...prev,
      pageSize,
      page: 1, // Reset to first page on page size change
    }));
  }, []);

  const handleExport = useCallback(() => {
    exportCsv({
      search: filters.search,
      status: filters.status,
    });
  }, [exportCsv, filters.search, filters.status]);

  // Show loading while checking user
  if (!userLoaded) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-4 w-32 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Show 403 if not Super Admin
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">{t('accessDenied.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('accessDenied.superAdminRequired')}
          </p>
          <button
            type="button"
            className="text-primary-600 hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">Failed to load organizations. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
            <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Organizations</h1>
            <p className="text-sm text-muted-foreground">
              {data?.pagination.totalCount ?? 0} total organizations
            </p>
          </div>
        </div>

        {/* Filters */}
        <OrganizationsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExport={handleExport}
          isExporting={isExporting}
        />

        {/* Table */}
        <OrganizationsTable
          organizations={data?.organizations ?? []}
          pagination={
            data?.pagination ?? {
              page: 1,
              pageSize: 10,
              totalCount: 0,
              totalPages: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            }
          }
          isLoading={isLoading}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
        />
      </div>
    </div>
  );
}
